import { APICredentials } from "api";
import moment from "moment";
import { Credentials } from "../state";
import { auth0SessionToCredentials, getStoredCredentials, removeStoredAuthState, setStoredCredentials } from "../util";
import { refreshAccessToken } from "./WebAuth";

let refreshPromise: Promise<string>;

function refreshToken(refreshToken: string): Promise<string> {
    if (!refreshToken) {
        return Promise.reject(new Error("Login Required."));
    }
    // If we're already waiting on a refresh, then just use that.
    if (refreshPromise) {
        return refreshPromise;
    }
    refreshPromise = Promise.resolve()
        .then(() => refreshAccessToken({ refreshToken }))
        .then(session => {
            const credentials = auth0SessionToCredentials({
                accessToken: session.access_token,
                expiresIn: session.expires_in,
                refreshToken: session.refresh_token,
            });
            setStoredCredentials(credentials);
            return credentials.accessToken;
        })
        .catch(e => {
            console.error("E", e);
            refreshPromise = undefined;
            removeStoredAuthState();
            // There may be another reason, but 99.9% of the time it's that a session cookie
            // expired and we need to get another one.
            throw new Error("Login Required.");
        })
        .then(result => {
            refreshPromise = undefined;
            return result;
        });
    return refreshPromise;
}

/**
 * This is a class that will provide credentials based on the elements
 * provided.
 *
 * @export
 * @class CredentialsProvider
 */
export class CredentialsProvider implements APICredentials {
    private localCreds: Credentials;
    private localAskCreds: string;

    constructor(credentials?: Credentials, askToken?: string) {
        this.localCreds = credentials;
        this.localAskCreds = askToken;
    }

    hasCredentials() {
        return !!this.localCreds;
    }

    get xappUserToken(): string | Promise<string> {
        // If we're waiting on a refresh token then just continue on with that.
        if (refreshPromise) {
            return refreshPromise;
        }

        // Else retrieve the local credentials.  If they are expired, start a refresh.
        const localCreds = this.getLocalCredentials();
        if (localCreds) {
            const expiration = moment(localCreds.Expiration);
            if (expiration.isBefore(moment())) {
                return refreshToken(localCreds.refreshToken);
            }
            return localCreds.accessToken;
        }
        return undefined;
    }

    get askToken(): string {
        return this.localAskCreds;
    }

    private getLocalCredentials() {
        if (this.hasCredentials()) {
            return this.localCreds;
        }
        return (this.localCreds = getStoredCredentials());
    }
}
